<template>
  <div class="period_setting">
    <flat-pickr v-model="dateRange" :config="config" @on-change="updateDate" />
    <custom-selectbox
      :class="['month_select', $i18n.locale, monthSelected === -1 ? 'initial' : '']"
      v-model="monthSelected"
      :items="monthArr"
      :firstItem="$t('date.selectMonth')"
      @custom-select="handleMonth"
    />
    <custom-selectbox
      :class="['month_select', $i18n.locale, monthSelected === -1 ? 'initial' : '']"
      :value="exchangeListStatus"
      :items="cateArr"
      @custom-select="handleCate"
    />
    <v-button
      class="btn05"
      @handleClick="handleClick"
      iconClass="icon_search"
      :text="$t('common.btnSearch')"
    ></v-button>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import VButton from '@/components/atoms/VButton';
import CustomSelectbox from '@/components/atoms/CustomSelectbox';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import { Korean } from 'flatpickr/dist/l10n/ko.js';
import { english } from 'flatpickr/dist/l10n/default.js';
import { MandarinTraditional } from 'flatpickr/dist/l10n/zh-tw.js';
import { Japanese } from 'flatpickr/dist/l10n/ja.js';
import { MONTH_NAME_LIST, GLOBAL_WITHDRAW_REQUEST_STATUS_BY_EXCHANGE_STATUS } from '@/consts/consts';
import { checkUserRegion } from '@/utils';

export default {
  components: {
    flatPickr,
    VButton,
    CustomSelectbox,
  },
  props: {
    cateArr: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      now: '',
      dateRange: '',
      config: {
        static: true,
        altFormat: 'Y-m-d',
        altInput: true,
        dateFormat: 'Y-m-d',
        mode: 'range',
        locale: this.dateLocale,
        monthSelectorType: 'static',
        maxDate: this.$moment().format('YYYY-MM-DD'),
      },
      monthSelected: -1,
      monthArr: [],
    };
  },
  watch: {
    '$i18n.locale': function () {
      this.setDateLocale();
    },
  },
  computed: {
    ...mapGetters('exchange', [
      'exchangeListPageSize',
      'exchangeListPageNo',
      'exchangeListStartDate',
      'exchangeListEndDate',
      'exchangeListStatus',
    ]),
    dateLocale() {
      switch (this.$i18n.locale) {
        case 'ko':
          return Korean;
        case 'en':
          return english;
        case 'zh':
          return MandarinTraditional;
        case 'ja':
          return Japanese;
        default:
          return english;
      }
    },
    dateMiddleText() {
      return this.$i18n.t('date.middle');
    },
    userRegion() {
      return checkUserRegion();
    },
  },
  mounted() {
    this.setDateLocale();
  },
  methods: {
    ...mapActions('modal', ['openModal']),
    ...mapActions('exchange', [
      'getExchangeHistory',
      'getGlobalExchangeHistory',
      'setExchangeListPageNo',
      'setExchangeListStartDate',
      'setExchangeListEndDate',
      'setExchangeListStatus',
    ]),
    setDateLocale() {
      this.config.locale = this.dateLocale;
      this.now = this.$moment();
      this.dateRange = this.exchangeListStartDate + this.dateMiddleText + this.exchangeListEndDate;
      this.setMonthArr();
    },
    updateDate(selectedDates) {
      if (selectedDates.length === 2) {
        this.setExchangeListStartDate(this.$moment(selectedDates[0]).format('YYYY-MM-DD'));
        this.setExchangeListEndDate(this.$moment(selectedDates[1]).format('YYYY-MM-DD'));
      }
    },
    handleMonth() {
      if (this.monthSelected === -1) return;
      let startDate = '';
      let endDate = '';
      let middleText = this.dateMiddleText;
      startDate = this.$moment().subtract(this.monthSelected, 'months').startOf('month').format('YYYY-MM-DD');

      if (this.monthSelected === 0) {
        endDate = this.now.format('YYYY-MM-DD');
      } else {
        endDate = this.$moment().subtract(this.monthSelected, 'months').endOf('month').format('YYYY-MM-DD');
      }
      this.dateRange = startDate + middleText + endDate;
    },
    handleCate(val) {
      this.setExchangeListStatus(val);
    },
    async handleClick() {
      if (!this.checkTerm()) {
        this.openModal({
          type: 'alert',
          message: this.$i18n.t('error.search.month'),
        });
        return;
      }
      if (checkUserRegion() !== 'krpc') {
        this.setExchangeListPageNo(1);

        // cate arr 과 api 요청 시 status number의 번호가 달라서 별도로 변환해줘야함
        // 참고: https://confluence.nexon.com/display/NEXONCOIN/%5BRPWeb%5D+GlobalWithdraw
        const pointStatus = GLOBAL_WITHDRAW_REQUEST_STATUS_BY_EXCHANGE_STATUS[this.exchangeListStatus || 0];

        await this.getGlobalExchangeHistory({
          startDate: this.exchangeListStartDate,
          endDate: this.exchangeListEndDate,
          pointStatus,
          pageNo: this.exchangeListPageNo,
          pageSize: this.exchangeListPageSize,
        });
      } else {
        this.setExchangeListPageNo(1);
        await this.getExchangeHistory({
          startDate: this.exchangeListStartDate,
          endDate: this.exchangeListEndDate,
          exchangeStatus: this.exchangeListStatus,
          pageNo: this.exchangeListPageNo,
          pageSize: this.exchangeListPageSize,
        });
      }
    },
    setMonthArr() {
      this.monthArr = [];
      let year = Number(this.now.format('YYYY')),
        month = Number(this.now.format('M'));
      for (let i = 0; i <= 12; i++) {
        if (this.$i18n.locale === 'ko' || this.$i18n.locale === 'zh' || this.$i18n.locale === 'ja') {
          this.monthArr.push(`${year}${this.$i18n.t('date.year')} ${month}${this.$i18n.t('date.month')}`);
        } else if (this.$i18n.locale === 'en') {
          this.monthArr.push(`${MONTH_NAME_LIST[month - 1]}, ${year}`);
        }

        month--;
        if (month <= 0) {
          year--;
          month = 12;
        }
      }
    },
    checkTerm() {
      return this.$moment(this.exchangeListEndDate).diff(this.$moment(this.exchangeListStartDate), 'months') <= 12;
    },
  },
};
</script>

<style lang="scss">
@media screen and (min-width: 767px) {
  .period_setting {
    .month_select.en.initial {
      .selectbox {
        font-size: 13px;
      }
      .items {
        font-size: 13px;
      }
    }
  }
}
</style>
